import { IbanModalProps } from '../utils/types';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { IBANInput } from './IbanInput';
import { useTranslation } from 'react-i18next';

const IbanModal: React.FC<IbanModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  details,
  askBankName = true,
}) => {
  const { t } = useTranslation(['translation', 'dynamic']);
  const [formData, setFormData] = useState({
    bank: details ? details.bank : '',
    customer: details ? details.customer : '',
    iban: details ? details.iban : '',
  });
  const [iban, setIban] = useState(details ? details.iban : '');
  const [isIbanValid, setIsIbanValid] = useState<boolean | null>(null);
  const canSubmit = isIbanValid === true && iban.trim() !== '';

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    onSubmit({
      ...formData,
      iban,
      bank: askBankName === false ? '' : formData.bank,
    });
  };

  if (!isOpen) return null;

  // #108 (S3, r1-1): the portal renders outside the app's div.xc-portal; its
  // own root carries the class so the tokens and the restyled IBAN control
  // (.xc-portal .xc-input) apply inside the modal
  return createPortal(
    <div className="xc-portal">
      {/* Bootstrap Modal Backdrop */}
      <div
        className="modal-backdrop fade show custom-modal-overlay"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
      />

      {/* Bootstrap Modal */}
      <div
        className="modal fade show d-block custom-modal-container col-8"
        tabIndex={-1}
        style={{ zIndex: 1055 }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          onClick={e => e.stopPropagation()}
        >
          <div className="modal-content custom-modal-content bg-light text-dark">
            {/* Modal Header */}
            <div className="modal-header border-0 pb-2">
              <h5 className="modal-title fw-bold text-dark">
                {t('ibanForm.title')}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              />
            </div>

            {/* Modal Body */}
            <div className="modal-body pt-0 align-content-start text-start">
              <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
                {t('ibanForm.subtitle')}
              </p>

              {/* Address Details Section */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-dark">
                  {t('ibanForm.details')}
                </h6>

                {/* Bank */}
                {askBankName !== false && (
                  <div className="mb-3">
                    <label className="form-label text-muted small">
                      {t('ibanForm.bankName')}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.bank}
                      name="bank"
                      onChange={handleInputChange}
                      placeholder={
                        t('ibanForm.enterBankName') ?? 'Enter your bank name'
                      }
                    />
                  </div>
                )}

                {/* Customer */}
                <div className="mb-3">
                  <label className="form-label text-muted small">
                    {t('ibanForm.accountName')}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.customer}
                    name="customer"
                    onChange={handleInputChange}
                    placeholder={
                      t('ibanForm.customerName') ?? 'Enter your account name'
                    }
                  />
                </div>

                {/* Iban */}
                <div className="mb-3">
                  <label
                    className="form-label text-muted small"
                    htmlFor="iban-input"
                  >
                    {t('ibanForm.iban')}
                    <span className="text-primary">*</span>
                  </label>
                  <IBANInput
                    value={iban}
                    onChange={setIban}
                    onValidationChange={setIsIbanValid}
                    placeholder={
                      t('ibanForm.ibanExample') ??
                      'RO49 AAAA 1B31 0075 9384 0000'
                    }
                    showCountryCode={true}
                    showDetailedErrors={true}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer border-0 pt-0 custom-modal-footer">
              <button
                type="button"
                className="btn btn-sm w-100 py-3 text-dark fw-medium btn-light border border-dark"
                style={{
                  border: 'none',
                  borderRadius: '8px',
                }}
                onClick={() => handleSubmit()}
                disabled={!canSubmit}
              >
                <i className="bi bi-floppy"></i> {t('ibanForm.submit')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default IbanModal;
